import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Clock, BookOpen, Calendar, AlertCircle, GraduationCap } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import { cbtService } from '../../services/cbtService'

const CBTWaitingRoom = () => {
  const { examId } = useParams()
  const navigate = useNavigate()
  const [examStatus, setExamStatus] = useState(null)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    checkExamStatus()
    
    // Check status every 5 seconds
    const pollInterval = setInterval(checkExamStatus, 5000)
    
    // Local countdown timer - update every second
    const countdownInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev > 0) return prev - 1
        return 0
      })
    }, 1000)
    
    return () => {
      clearInterval(pollInterval)
      clearInterval(countdownInterval)
    }
  }, [examId])

  const checkExamStatus = async () => {
    try {
      const response = await cbtService.getExamStatus(examId)
      const status = response.data.data
      
      setExamStatus(status)
      setTimeRemaining(status.secondsUntilStart)
      
      // If exam has started, redirect to exam page
      if (status.isStarted) {
        navigate(`/cbt/exam/${examId}`)
      }
      
      setLoading(false)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check exam status')
      setLoading(false)
    }
  }

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No date'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  const formatTimeOnly = (timeString) => {
    if (!timeString) return 'No time'
    try {
      const [hours, minutes] = timeString.split(':')
      const date = new Date()
      date.setHours(parseInt(hours), parseInt(minutes))
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } catch (error) {
      return 'Invalid time'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6">
            <ErrorDisplay message={error} />
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/cbt')}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Return to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold text-gray-900">Exam Waiting Room</CardTitle>
          <p className="text-gray-600 mt-2">Please wait for the exam to begin</p>
        </CardHeader>
        
        <CardContent className="space-y-8">
          {/* Countdown Timer */}
          <div className="text-center">
            <div className="bg-white p-8 rounded-lg border-2 border-blue-200">
              <div className="flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-blue-600 mr-3" />
                <h2 className="text-2xl font-semibold text-gray-900">Exam Begins In</h2>
              </div>
              
              <div className="text-6xl font-mono font-bold text-blue-600 mb-4">
                {formatTime(timeRemaining)}
              </div>
              
              <p className="text-gray-600">
                {timeRemaining > 0 
                  ? 'Please remain seated and wait for the exam to start'
                  : 'Exam is starting now...'
                }
              </p>
            </div>
          </div>

          {/* Exam Information */}
          {examStatus && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Exam Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subject:</span>
                    <span className="font-medium">Mathematics</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">60 minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Marks:</span>
                    <span className="font-medium">100</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Questions:</span>
                    <span className="font-medium">25</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-medium">{formatDate(examStatus.examStartTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Time:</span>
                    <span className="font-medium">{formatTimeOnly(examStatus.examStartTime)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Current Time:</span>
                    <span className="font-medium">{formatTimeOnly(examStatus.currentTime)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Instructions */}
          <Card className="bg-yellow-50 border-yellow-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="w-5 h-5" />
                Important Instructions
              </CardTitle>
            </CardHeader>
            <CardContent className="text-yellow-800">
              <ul className="space-y-2 list-disc list-inside">
                <li>Do not refresh the page or navigate away</li>
                <li>Ensure you have a stable internet connection</li>
                <li>Close all other applications and browser tabs</li>
                <li>Do not use any external resources during the exam</li>
                <li>The exam will start automatically when the timer reaches zero</li>
                <li>If you experience technical issues, raise your hand for assistance</li>
              </ul>
            </CardContent>
          </Card>

          {/* Status Indicator */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Waiting for exam to begin...</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default CBTWaitingRoom
